import {
  AgxBodyText,
  AgxColumn,
  AgxDivider,
  AgxHeader,
  AgxRow,
  AgxLabel,
  formatCurrency,
} from '@urbanx/agx-ui-components'
import './UpcomingSettlements.scss'
import {
  AgxExpandableContent,
  UpcomingSettlementRow,
  UpcomingSettlementRowLoader,
  UpcomingSettlementTotalRow,
} from '@urbanx/agx-ui-components'
import { UpcomingSettlementsForAgency } from 'services/functions/rex/useStaffGetUpcomingSettlementsForAgency'
import { UpcomingSettlement } from '@urbanx/agx.ui-components2'

interface UpcomingSettlementProps {
  loading: boolean
  upcomingSettlementsForAgency?: UpcomingSettlementsForAgency
}

const NoUpcomingSettlementsGroup = () => {
  return (
    <AgxColumn extraClasses="upcomingSettlements noContent">
      <AgxColumn fill centered extraLargeGap extraClasses="noDataDiv">
        <AgxBodyText centered>No upcoming settlements</AgxBodyText>
      </AgxColumn>
    </AgxColumn>
  )
}

const UpcomingSettlementsGroup = ({
  loading,
  upcomingSettlements,
}: {
  loading: boolean
  upcomingSettlements: UpcomingSettlement[]
}) => {
  return (
    <div className="upcomingSettlementsHeader">
      <AgxColumn extraClasses="upcomingSettlements">
        <div className="upcomingSettlementRow header">
          <AgxColumn extraClasses="upcomingSettlement" centerJustified>
            <AgxBodyText small>Property</AgxBodyText>
          </AgxColumn>
          <AgxColumn
            extraClasses="upcomingSettlement settlement"
            centerJustified
          >
            <AgxBodyText small>Settlement</AgxBodyText>
          </AgxColumn>
          <AgxColumn
            extraClasses="upcomingSettlement commission"
            centerJustified
          >
            <AgxBodyText small>Commission</AgxBodyText>
          </AgxColumn>
        </div>
        {loading &&
          [...Array(10)].map((_, idx) => (
            <>
              <UpcomingSettlementRowLoader key={idx} />
              {idx < 10 && <AgxDivider />}
            </>
          ))}
        {!loading &&
          upcomingSettlements &&
          upcomingSettlements.length > 0 &&
          upcomingSettlements?.map((settlement, idx: number) => (
            <UpcomingSettlementRow key={idx} settlements={settlement} />
          ))}
        {!loading && upcomingSettlements.length > 0 && (
          <UpcomingSettlementTotalRow settlements={upcomingSettlements} />
        )}
      </AgxColumn>
    </div>
  )
}

const ExpandableSettlementsSection = ({
  upcomingSettlementsOnOrAfterAnniversary,
}: {
  upcomingSettlementsOnOrAfterAnniversary: UpcomingSettlement[] | undefined
}) => {
  return (
    <AgxExpandableContent
      isExpanded={false}
      key={'UpcomingSettlementsExpandableContent'}
      title={'Show settlements past Anniversary date'}
    >
      <AgxColumn extraClasses="upcomingSettlements beforeAnniversaryTable">
        {upcomingSettlementsOnOrAfterAnniversary?.map(
          (settlement, idx: number) => (
            <UpcomingSettlementRow key={idx} settlements={settlement} />
          )
        )}
      </AgxColumn>
    </AgxExpandableContent>
  )
}

const ExpandableNoSettlementsSection = () => {
  return (
    <AgxExpandableContent
      isExpanded={false}
      key={'NoUpcomingSettlementsExpandableContent'}
      title={'Show settlements past Anniversary date'}
    >
      <AgxColumn extraClasses="upcomingSettlements noContent">
        <AgxColumn fill centered extraLargeGap extraClasses="noDataDiv">
          <AgxBodyText centered>No upcoming future settlements</AgxBodyText>
        </AgxColumn>
      </AgxColumn>
    </AgxExpandableContent>
  )
}

const UpcomingSettlements = ({
  loading,
  upcomingSettlementsForAgency,
}: UpcomingSettlementProps) => {
  const { upcomingSettlements, settlementsAfterAnniversary } =
    upcomingSettlementsForAgency || {
      upcomingSettlements: [],
      settlementsAfterAnniversary: [],
    }

  return (
    <>
      <AgxColumn extraClasses="upcomingSettlementsBody">
        <AgxRow fill spaceBetween>
          <AgxHeader size={5}>Upcoming Settlements</AgxHeader>
          <AgxLabel>
            Total:&nbsp;
            {formatCurrency(
              (upcomingSettlements || []).reduce(
                (sum, s) => sum + (s.commission ?? 0),
                0
              )
            )}
          </AgxLabel>
        </AgxRow>
        {upcomingSettlements.length === 0 && <NoUpcomingSettlementsGroup />}
        {upcomingSettlements.length > 0 && (
          <UpcomingSettlementsGroup
            loading={loading}
            upcomingSettlements={upcomingSettlements}
          />
        )}
        {settlementsAfterAnniversary.length === 0 && (
          <ExpandableNoSettlementsSection />
        )}
        {settlementsAfterAnniversary.length > 0 && (
          <ExpandableSettlementsSection
            upcomingSettlementsOnOrAfterAnniversary={
              settlementsAfterAnniversary
            }
          />
        )}
      </AgxColumn>
    </>
  )
}

export default UpcomingSettlements
